<template>
	<div class="video-list">
		<EventsSection
			:pagination="videos.pagination"
			:handle-pagination-change="handlePaginationChange"
			:events="videos.items"
			:is-loading="videos.isLoading"
			:show-results-counter="false"
		>
			<div
				v-if="showResultsCount"
				class="video-list__results-count"
			>
				{{ videos.pagination.total }} {{ $I18n.trans_choice('commons.video', videos.pagination.total) }}
			</div>
			<Select
				v-if="enableSortOrder"
				class="video-list__select-order"
				:options="sortOrder.options"
				:value="sortOrder.selected"
				:searchable="false"
				:clearable="false"
				:dark-theme="!$arenaConfig.isLightTheme"
				:rounded="true"
				@change="handleSortOrderChange"
			/>
		</EventsSection>
	</div>
</template>

<script>
import EventsSection from '@js/components/EventListing/EventsSection';
import Select from '@shared/components/Select';
import { EventOrder } from '@shared/enums/EventOrder';
import { EventFilter } from '@shared/enums/EventFilter';
import { httpClient } from '@shared/js/api/httpClient';

export default {
	name:       'VideoList',
	components: { EventsSection, Select },
	props:      {
		enableSortOrder: {
			type:    Boolean,
			default: true,
		},
		contextFilter: {
			type:     Object,
			required: true,
		},
		eventFilters: {
			type:    Object,
			default: () => ({}),
		},
		externalSortOrder: {
			type:    Object,
			default: () => ({}),
		},
		showResultsCount: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			sortOrder: {
				options: [
					{
						label: this.$I18n.trans('commons.date'),
						value: 'date',
					},
					{
						label: this.$I18n.trans('commons.views'),
						value: 'views',
					},
				],
				selected:  'date',
				direction: 'desc',
			},
			videos: {
				isLoading:  false,
				pagination: this.getInitialPagination(24),
				items:      [],
			},
		};
	},
	mounted() {
		if (this.externalSortOrder && Object.keys(this.externalSortOrder).includes('start')) {
			this.sortOrder.selected = 'date';
			this.sortOrder.direction = this.externalSortOrder.start;
		}

		this.getVideos();
	},
	methods: {
		resetPaginationAndFetch(perPage = 24) {
			this.videos.pagination.currentPage = 1;
			this.videos.pagination.perPage = perPage;
			this.getVideos();
		},
		handlePaginationChange(page) {
			this.videos.pagination.currentPage = page;
			this.getVideos();
		},
		handleSortOrderChange(value) {
			this.sortOrder.selected = value;
			this.resetPaginationAndFetch();
		},
		async fetchData(searchParams) {
			const response = await httpClient.getEventsCached(Object.fromEntries(new URLSearchParams(searchParams)));
			return response.data;
		},
		async getVideos() {
			try {
				this.videos.isLoading = true;

				const searchParams = this.getSearchParams();
				const data = await this.fetchData(searchParams);
				this.videos.items = data.data;
				this.videos.pagination = data.meta;
			} catch (error) {
				// shows empty list
			} finally {
				this.videos.isLoading = false;
			}
		},
		getSearchParams() {
			const searchParams = new URLSearchParams();

			// filter(s) parameter
			const filters = this.eventFilters || {};
			filters[EventFilter.ARENA_AVAILABLE] = null;

			// Apply context filter
			filters[this.contextFilter.key] = this.contextFilter.value;

			searchParams.append('filters', JSON.stringify(filters));

			if (this.enableSortOrder && this.sortOrder.selected === 'views') {
				searchParams.append('sortOrder', JSON.stringify({
					[EventOrder.VIEWS]: 'desc',
				}));
			} else {
				searchParams.append('sortOrder', JSON.stringify({
					[EventOrder.START]: this.sortOrder.direction,
				}));
			}

			// pagination parameters
			searchParams.append('page', JSON.stringify(this.videos.pagination.currentPage));
			searchParams.append('perPage', JSON.stringify(this.videos.pagination.perPage));

			return searchParams;
		},
		getInitialPagination(perPage) {
			return {
				perPage:     perPage,
				currentPage: 1,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.video-list {
	margin-bottom: $sp7;

	@include size(md-up) {
		margin-bottom: $sp9;
	}
}

.video-list__results-count {
	@include font(primary, book, normal, fs-80);
	color: var(--color-grey-300);
	margin-bottom: 0;
}

.video-list__select-order {
	margin-bottom: $sp5;
	margin-left: auto;
	width: 200px;

	@include size(md-up) {
		width: 240px;
	}
}
</style>
